import { html } from "htm/preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { getClient } from "../client.js";
import { NotJoined } from "../components/NotJoined.js";
import { forgetTournament, getAuth } from "../storage.js";
import { navigate } from "../router.js";
import { loadCourse } from "../courseData.js";
import { computeLiveRoundResults, type LiveRoundResult } from "../../../shared/leaderboard.js";
import { computeFinalStandings, type StandingEntry } from "../../../shared/standings.js";
import { exportTournamentJson, exportTournamentText } from "../http.js";
import { ShareButton } from "../components/ShareButton.js";
import { Icon } from "../components/Icon.js";
import { FLIP_MS, useCountUp, useFlipList, useRecentDelta } from "../animation.js";
import { takePendingDonMode, useDonMode, useHeist } from "../donMode.js";
import { crownStandings, stealPoints } from "../../../shared/donMode.js";
import type { Course } from "../../../shared/types.js";
import { TERMS } from "../copy.js";

const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;

/** Shown while the first snapshot is still in flight. Sized like the real board so
 * arriving at the screen doesn't flash a line of text and then jump. */
function StandingsSkeleton() {
  return html`
    <h2>Standings</h2>
    <div class="lb">
      ${[0, 1, 2, 3].map(
        (i) => html`<div class="lb-row skeleton-row" key=${i} style=${`--i:${i}`}></div>`,
      )}
    </div>
  `;
}

/**
 * The standings list. Owns the reordering animation for the whole list, which is why
 * it exists as its own component rather than being inlined in the screen: the FLIP
 * hook needs the running order, and that isn't known until after the screen's
 * loading guard.
 */
function StandingsBoard({
  code,
  standings,
  ranked,
  live,
  nameOf,
  projected,
  crownedId,
  thiefId,
  stealing,
  stolen,
}: {
  code: string;
  standings: StandingEntry[];
  ranked: boolean;
  live: boolean;
  nameOf: (id: string) => string;
  projected: (id: string) => number;
  crownedId: string | null;
  thiefId: string | null;
  stealing: boolean;
  stolen: Map<string, number>;
}) {
  const register = useFlipList(standings.map((s) => s.playerId).join(","));
  // The heist is drawn over the whole board rather than by any one row, so it's
  // launched from here — this is the component that owns the list.
  const board = useRef<HTMLDivElement | null>(null);
  useHeist(board, stealing, thiefId, stolen);

  return html`
    <div class="lb" ref=${board}>
      ${standings.map(
        (s, i) => html`
          <${StandingRow}
            key=${s.playerId}
            playerId=${s.playerId}
            index=${i}
            placement=${s.placement}
            name=${nameOf(s.playerId)}
            points=${s.totalPoints}
            projectedPoints=${projected(s.playerId)}
            ranked=${ranked}
            live=${live}
            register=${register}
            crowned=${s.playerId === crownedId}
            robbed=${thiefId !== null && s.playerId !== thiefId}
            onOpen=${() => navigate(`/t/${code}/player/${s.playerId}`)}
          />
        `,
      )}
    </div>
  `;
}

/**
 * One player's line on the board.
 *
 * A separate component because the animation is per-row state — a row has to
 * remember its own last placement and tween its own numbers, and hooks can't live
 * inside a `.map()` over a list whose length changes.
 */
function StandingRow({
  placement,
  name,
  points,
  projectedPoints,
  ranked,
  live,
  index,
  register,
  playerId,
  crowned,
  robbed,
  onOpen,
}: {
  placement: number;
  name: string;
  points: number;
  projectedPoints: number;
  ranked: boolean;
  live: boolean;
  index: number;
  register: (key: string) => (el: HTMLElement | null) => void;
  playerId: string;
  crowned: boolean;
  robbed: boolean;
  onOpen: () => void;
}) {
  // The stagger delay is frozen at mount. `--i` feeds an animation-delay, and
  // changing it later re-triggers the entrance animation — so a row that moved would
  // fade itself back in while it was still sliding.
  const enterIndex = useRef(index).current;
  // Placement counts down as you climb, so a gain is a negative change. The cue waits
  // for the rows to finish travelling — it's the caption on the movement, not a
  // fifth thing happening during it.
  const climbed = -useRecentDelta(placement, { enabled: live, delay: FLIP_MS });
  const shownPoints = useCountUp(points);
  const shownProjected = useCountUp(projectedPoints);

  const moveClass = climbed > 0 ? "moved-up" : climbed < 0 ? "moved-down" : "";

  return html`
    <button
      class=${`lb-row ${ranked && placement === 1 ? "leader" : ""} ${moveClass} ${
        crowned ? "crowned" : ""
      } ${robbed ? "robbed" : ""}`}
      style=${`--i:${enterIndex}`}
      ref=${register(playerId)}
      data-player=${playerId}
      onClick=${onOpen}
      aria-label=${`${name} — how this total was worked out`}
    >
      <span class=${`rank ${ranked ? `rank-${placement}` : ""}`}>${placement}</span>
      <span class="lb-name">${name}</span>
      ${crowned && html`<${Icon} name="trophy" size=${15} class="icon crown" />`}
      ${climbed !== 0 &&
      html`<span class=${`rank-move ${climbed > 0 ? "up" : "down"}`}>
        <${Icon} name=${climbed > 0 ? "arrow-up" : "arrow-down"} size=${13} />
        ${Math.abs(climbed)}
      </span>`}
      <span class="lb-points">
        ${shownPoints}
        ${projectedPoints > 0 && html`<span class="projected">${`+${shownProjected}`}</span>`}
      </span>
      <${Icon} name="right" size=${16} class="icon lb-chevron" />
    </button>
  `;
}

/**
 * Where a round has got to.
 *
 * Four states, not three: a card with every hole in but no sign-off is its own thing,
 * and it used to fall through to "not started" — which is how a finished round ended
 * up labelled as one nobody had teed off in. It gets the loudest chip, because a
 * finished round is exactly when the organiser needs to do something.
 */
function RoundStatusChip({ result }: { result: LiveRoundResult | undefined }) {
  if (!result || result.holesScored === 0) return html`<span class="chip">not started</span>`;

  if (result.provisional) {
    return result.holesScored === result.holesTotal
      ? html`<span class="chip chip-accent">
          <span class="dot"></span> ${`all ${result.holesTotal} in · close to confirm`}
        </span>`
      : html`<span class="chip chip-accent">
          <span class="dot"></span> ${`live · ${result.holesScored}/${result.holesTotal}`}
        </span>`;
  }

  return html`<span class="chip">
    ${result.holesScored < result.holesTotal
      ? `closed · ${result.holesScored} of ${result.holesTotal} holes`
      : "closed"}
  </span>`;
}

/**
 * The round's name, as the way into its hole-by-hole breakdown.
 *
 * The name rather than a button beside it: a third control on this row crowded the
 * status chip out of its own card, and the card's player rows are already links of
 * their own, so making the whole card clickable would swallow them.
 */
function RoundNameLink({
  name,
  result,
  onOpen,
}: {
  name: string;
  result: LiveRoundResult | undefined;
  onOpen: () => void;
}) {
  return html`
    <div
      class="grow round-name-link"
      onClick=${onOpen}
      tabindex="0"
      role="button"
      onKeyDown=${(e: KeyboardEvent) => (e.key === "Enter" || e.key === " ") && onOpen()}
      aria-label=${`${name} — hole by hole`}
    >
      <strong>
        ${name}
        <${Icon} name="right" size=${14} class="icon row-link-chevron" />
      </strong>
      <div class="mt-1"><${RoundStatusChip} result=${result} /></div>
    </div>
  `;
}

/** A player's line inside a round card. Its points move hole by hole while the round
 * is being played, which is the one figure on this screen that's live mid-round. */
function RoundPointsRow({
  name,
  points,
  onOpen,
}: {
  name: string;
  points: number;
  onOpen: () => void;
}) {
  return html`
    <tr class="row-link" onClick=${onOpen} tabindex="0" role="button"
      onKeyDown=${(e: KeyboardEvent) => (e.key === "Enter" || e.key === " ") && onOpen()}
      aria-label=${`${name} — how this round scored`}>
      <td>${name}</td>
      <td>
        ${useCountUp(points)}
        <${Icon} name="right" size=${14} class="icon row-link-chevron" />
      </td>
    </tr>
  `;
}

export function Leaderboard({ code, notice }: { code: string; notice?: string }) {
  const auth = getAuth(code);
  const client = getClient(code);
  const snapshot = client?.snapshot.value ?? null;
  const [courses, setCourses] = useState<Map<string, Course>>(new Map());
  const [exportMsg, setExportMsgRaw] = useState<{ text: string; ok: boolean } | null>(null);
  const setExportMsg = (text: string | null) => setExportMsgRaw(text ? { text, ok: true } : null);
  const setExportError = (text: string) => setExportMsgRaw({ text, ok: false });
  const [leaving, setLeaving] = useState(false);
  const hydrated = client?.hydrated.value ?? false;
  // Called up here with the other hooks because the loading guard below returns early.
  // The flag rides on the tournament, not on this device, which is what puts the same
  // minute on everyone's board at once — including whoever isn't looking at Don.
  const don = useDonMode(snapshot?.tournament.donMode ?? null, () =>
    client?.submit({ type: "donMode" }),
  );

  const courseIds = useMemo(
    () => [...new Set((snapshot?.tournament.rounds ?? []).map((r) => r.courseId))].filter(Boolean),
    [snapshot],
  );

  useEffect(() => {
    Promise.all(courseIds.map((id) => loadCourse(id))).then((loaded) => {
      const map = new Map<string, Course>();
      loaded.forEach((c) => c && map.set(c.id, c));
      setCourses(map);
    });
  }, [courseIds.join(",")]);

  // Whether the board is working from complete inputs. Rounds can't be scored before
  // their course has loaded, and a course is fetched asynchronously even from the
  // cache, so a freshly mounted board spends its first frames with everyone level on
  // nothing. Two things wait on it. The movement cues (see `useRecentDelta`), because
  // the placements separating as the courses arrive is the screen finishing loading,
  // not anybody overtaking anybody — and the theft below.
  const settledInputs = hydrated && courseIds.every((id) => courses.has(id));

  // Don fires this from his own breakdown screen, which then sends him here — and
  // waiting is the whole reason it arrives as an intent rather than as an op. The
  // theft opens with every rival's total draining away, and there is nothing to drain
  // on a board still filling in: firing on arrival robbed four players of nothing.
  const [pendingDon, setPendingDon] = useState(() => takePendingDonMode());
  useEffect(() => {
    if (!pendingDon) return;
    const fire = () => {
      setPendingDon(false);
      don.activate();
    };
    if (settledInputs) {
      fire();
      return;
    }
    // ...but a press must never do nothing. A round whose course can't be fetched
    // leaves the board unsettled for good, and a phone with no signal never hydrates,
    // so give up waiting for the pretty version and take the points anyway.
    const timer = setTimeout(fire, 1500);
    return () => clearTimeout(timer);
  }, [pendingDon, settledInputs]);

  // A null client means no stored token, and no token means no snapshot is ever
  // coming — so this has to be told apart from a snapshot that is merely still in
  // flight, or the skeleton below becomes permanent.
  if (!client) return html`<${NotJoined} code=${code} />`;
  if (!snapshot) return html`<${StandingsSkeleton} />`;

  const { tournament, scores, perHoleData } = snapshot;
  const roundResults = computeLiveRoundResults(tournament, courses, scores, perHoleData);

  // Confirmed points come from finished rounds only; rounds still being played are
  // shown separately as a running projection, so a leader isn't implied by a round
  // that is half-scored.
  const settled = roundResults.filter((r) => !r.provisional);
  const inPlay = roundResults.filter((r) => r.provisional);
  const standings = computeFinalStandings(
    tournament.players.map((p) => p.id),
    settled,
    tournament.finalTiebreaker,
  );
  const projected = (playerId: string) =>
    inPlay.reduce((sum, r) => sum + (r.points[playerId] ?? 0), 0);
  const anyInPlay = inPlay.length > 0;
  const nameOf = (id: string) => tournament.players.find((p) => p.id === id)?.name ?? id;

  // Don mode (`shared/donMode.ts`) — the app's easter egg, and the only place the
  // rendered board differs from the computed one. `standings` stays the real thing:
  // the round cards below and both exports read from it, so a minute of robbery
  // changes nothing that was scored. `board` is what gets drawn, in two beats — first
  // every point on the board becomes Don's, then he climbs to the top of it.
  const loot = don.thiefId ? stealPoints(standings, don.thiefId) : standings;
  const board = don.crownedId ? crownStandings(loot, don.crownedId) : loot;
  // What each player is about to lose, for the tokens that fly across the board.
  const stolen = new Map(standings.map((s) => [s.playerId, s.totalPoints]));
  // Points riding on unfinished rounds go the same way — leaving them behind would
  // have a robbed player sitting on "0 +6", which reads as a rendering fault rather
  // than as a robbery.
  const pot = standings.reduce((sum, s) => sum + projected(s.playerId), 0);
  const projectedShown = don.thiefId
    ? (id: string) => (id === don.thiefId ? pot : 0)
    : projected;
  // Before any round has finished everyone is level on nothing, and marking four joint
  // leaders in gold says something the scores don't. Rank styling only appears once
  // someone is actually ahead.
  const ranked = new Set(board.map((s) => s.placement)).size > 1;
  const expiresAt = new Date(tournament.lastActivityAt + SIXTY_DAYS_MS);
  // A spectator never joined anything, so "leave" overstates what they'd be undoing.
  const leaveLabel =
    auth?.role === "spectator" ? "Stop watching" : `Leave this ${TERMS.event}`;

  /**
   * Neither of these used to be able to fail visibly, and both can fail routinely.
   *
   * `navigator.clipboard.writeText` is the sharper one: it needs the page to still hold
   * transient user activation, and awaiting the export first is exactly what spends it,
   * so on iOS Safari — most of this app's traffic, on a course — the copy rejects every
   * time. That rejection had nowhere to go, so the button looked like it did nothing.
   * Fetching before the click is what keeps the activation intact.
   */
  async function copyText() {
    setExportMsg(null);
    try {
      const text = await exportTournamentText(code);
      try {
        await navigator.clipboard.writeText(text);
        setExportMsg("Copied to clipboard.");
      } catch {
        // Denied clipboard permission, an insecure origin, or a spent activation. The
        // text is in hand either way, so offer it rather than swallowing the failure.
        setExportError("Couldn't reach the clipboard on this browser — use the JSON download instead.");
      }
    } catch (err) {
      setExportError(`Couldn't build the export: ${(err as Error).message}`);
    }
  }

  async function downloadJson() {
    setExportMsg(null);
    try {
      const data = await exportTournamentJson(code);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tournament.code}.json`;
      // Firefox ignores a click on an anchor that was never in the document, and
      // revoking the URL in the same tick can beat the download to it.
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      setExportMsg("Downloaded.");
    } catch (err) {
      setExportError(`Couldn't build the export: ${(err as Error).message}`);
    }
  }

  return html`
    ${notice && html`<p class="notice mb-3"><${Icon} name="eye" size=${16} /> ${notice}</p>`}
    <h1 class="mb-0">${tournament.name}</h1>
    <p class="course-line">
      Code <strong class="mono">${code}</strong> ·
      ${` deleted on ${expiresAt.toLocaleDateString()} if unused`}
    </p>
    <${ShareButton} code=${code} name=${tournament.name} />

    ${tournament.declaredWinnerId &&
    html`<p class="winner-banner">
      <${Icon} name="trophy" size=${20} />
      ${`${nameOf(tournament.declaredWinnerId)} — declared by the ${TERMS.organizer}`}
    </p>`}

    ${don.thiefId &&
    html`<p class=${`don-banner ${don.stealing ? "is-robbery" : ""}`}>
      <span class="don-title">
        <${Icon} name="trophy" size=${18} />
        ${don.stealing ? "Taking everything" : `${nameOf(don.thiefId)} mode`}
        ${!don.stealing && html`<span class="don-clock">${`${don.secondsLeft}s`}</span>`}
      </span>
      <small>
        ${don.stealing ? "Nobody saw anything." : "Undefeated. Holding everyone's points."}
      </small>
    </p>`}
    ${don.ended &&
    html`<p class="don-banner is-over">
      <span class="don-title">Points returned</span>
      <small>No charges were filed.</small>
    </p>`}

    <h2>Standings</h2>
    <${StandingsBoard}
      code=${code}
      standings=${board}
      ranked=${ranked}
      live=${settledInputs}
      nameOf=${nameOf}
      projected=${projectedShown}
      crownedId=${don.crownedId}
      thiefId=${don.thiefId}
      stealing=${don.stealing}
      stolen=${stolen}
    />

    ${anyInPlay &&
    html`<p class="field-help projected-note">
      ${`+ figures come from ${inPlay.length === 1 ? "a round" : `${inPlay.length} rounds`} the ${TERMS.organizer} hasn't closed yet, counting only holes everyone has finished. They reach the standings when the round is closed.`}
    </p>`}

    <h2>Rounds</h2>
    <div class="stack">
      ${tournament.rounds.map((round, i) => {
        const result = roundResults[i];
        return html`
          <div class="card m-0" key=${round.id}>
            <div class="row row-between">
              <${RoundNameLink}
                name=${round.name}
                result=${result}
                onOpen=${() => navigate(`/t/${code}/round/${round.id}/holes`)}
              />
              <button class="secondary" onClick=${() => navigate(`/t/${code}/round/${round.id}`)}>
                Open
              </button>
            </div>
            <table class="scorecard-table mt-3">
              <tbody>
                ${tournament.players.map(
                  (p) => html`
                    <${RoundPointsRow}
                      key=${p.id}
                      name=${p.name}
                      points=${result?.points[p.id] ?? 0}
                      onOpen=${() => navigate(`/t/${code}/round/${round.id}/player/${p.id}`)}
                    />
                  `,
                )}
              </tbody>
            </table>
          </div>
        `;
      })}
    </div>

    <div class="panel mt-5">
      <span class="eyebrow">Export</span>
      <div class="row mt-2">
        <button class="secondary grow" onClick=${copyText}>
          <${Icon} name="copy" size=${18} /> Copy text
        </button>
        <button class="secondary grow" onClick=${downloadJson}>
          <${Icon} name="download" size=${18} /> JSON
        </button>
      </div>
      ${exportMsg &&
      html`<p class=${`notice mt-3 ${exportMsg.ok ? "notice-ok" : "notice-error"}`}>
        ${exportMsg.text}
      </p>`}

      <hr class="divider" />

      <span class="eyebrow">${leaveLabel}</span>
      <p class="field-help">
        ${`Removes it from this phone. It keeps running for everyone else, and ${
          auth?.role === "admin" ? "your organizer link" : "the code"
        } gets you back in.`}
      </p>
      ${leaving
        ? html`
            <div class="card card-danger">
              <p>
                ${auth?.role === "spectator"
                  ? `Stop watching "${tournament.name}" on this device?`
                  : `Leave "${tournament.name}" on this device?`}
              </p>
              <div class="row">
                <button
                  class="danger grow"
                  onClick=${() => {
                    forgetTournament(code);
                    navigate("/");
                  }}
                >
                  ${auth?.role === "spectator" ? "Stop watching" : "Leave"}
                </button>
                <button class="secondary" onClick=${() => setLeaving(false)}>Cancel</button>
              </div>
            </div>
          `
        : html`<button class="secondary danger-quiet" onClick=${() => setLeaving(true)}>
            ${leaveLabel}
          </button>`}
    </div>
  `;
}
